import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { storeAuthHandoffPayload } from 'app/auth/helpers/auth-handoff.util';

const HOSTED_HUB = 'https://front-hub.agrosilo-solutions.com';
const LOCAL_PORT_TO_HOSTED: Record<string, string> = {
  '4190': HOSTED_HUB,
  '4300': 'https://front-parametrage.agrosilo-solutions.com',
  '4200': 'https://front-vente.agrosilo-solutions.com',
  '4205': 'https://front-gestion-stock.agrosilo-solutions.com',
  '4201': 'https://front-achat.agrosilo-solutions.com',
  '4202': 'https://front-comptabilite.agrosilo-solutions.com',
  '4203': 'https://front-paramatrage-donne.agrosilo-solutions.com',
  '4500': 'https://front-logiciel-caisse.agrosilo-solutions.com',
};

export function getHostedHubBaseUrl(): string {
  const fromEnv = String((environment as { hubUrl?: string }).hubUrl || '').trim();
  if (fromEnv && !/localhost|127\.0\.0\.1/i.test(fromEnv)) {
    return fromEnv.replace(/\/$/, '');
  }
  return HOSTED_HUB;
}

/** Remplace localhost:PORT par l’URL hébergée correspondante. */
export function rewriteLocalhostToHostedUrl(url: string | null | undefined): string {
  const raw = String(url || '').trim();
  if (!raw) {
    return raw;
  }
  try {
    const absolute = /^https?:\/\//i.test(raw)
      ? raw
      : `${window.location.origin}${raw.startsWith('/') ? raw : `/${raw}`}`;
    const u = new URL(absolute);
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') {
      return /^https?:\/\//i.test(raw) ? raw : `${u.pathname}${u.search}${u.hash}`;
    }
    const hostedBase = (LOCAL_PORT_TO_HOSTED[u.port] || HOSTED_HUB).replace(/\/$/, '');
    return `${hostedBase}${u.pathname}${u.search}${u.hash}`;
  } catch {
    return raw;
  }
}

/**
 * Si le setup LC tourne encore sur localhost, bascule vers le hub hébergé
 * en conservant le chemin + returnUrl.
 */
export function redirectHostedHubIfLocalhost(authQuery?: string | null): boolean {
  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return false;
  }
  const hub = getHostedHubBaseUrl();
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  let target = `${hub}${path.startsWith('/') ? path : `/${path}`}`;
  const auth = String(authQuery || '').trim();
  if (auth) {
    const sep = target.includes('?') ? '&' : '?';
    target = `${target}${sep}auth=${auth}`;
  }
  window.location.href = target;
  return true;
}

/**
 * Construit le payload session pour navigation cross-domaine.
 * Préfère cookie partagé (chaîne vide) ; sinon `auth=` base64 (localhost).
 */
export function buildCrossFrontAuthQueryParams(): string {
  const out: Record<string, string | null> = {
    token: localStorage.getItem('token') || localStorage.getItem('erp_token'),
    users: localStorage.getItem('users'),
    currentUser: localStorage.getItem('currentUser'),
  };
  for (const key of ['logiciel_caisse_token', 'logiciel_caisse_session'] as const) {
    const v = localStorage.getItem(key);
    if (v) {
      out[key] = v;
    }
  }
  try {
    const raw = localStorage.getItem('currentUser');
    const uid = raw ? Number(JSON.parse(raw)?.id) : 0;
    if (Number.isFinite(uid) && uid > 0) {
      for (const prefix of ['logiciel_caisse_code_', 'logiciel_caisse_access_cache_']) {
        const key = `${prefix}${uid}`;
        const v = localStorage.getItem(key);
        if (v) {
          out[key] = v;
        }
      }
    }
  } catch {
    /* ignore */
  }
  if (storeAuthHandoffPayload(out as Record<string, unknown>)) {
    return '';
  }
  return encodeURIComponent(btoa(JSON.stringify(out)));
}

/** Ajoute `?auth=` seulement si nécessaire (cookie handoff → URL inchangée). */
export function withCrossFrontAuth(url: string, authQuery?: string | null): string {
  const auth = String((authQuery ?? buildCrossFrontAuthQueryParams()) || '').trim();
  if (!auth || !url) {
    return url;
  }
  try {
    const u = new URL(url);
    u.searchParams.set('auth', decodeURIComponent(auth));
    return u.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}auth=${auth}`;
  }
}

/** Retour après setup LC : URL absolue (autre front) ou route locale hub. */
export function navigateLogicielCaisseReturnUrl(router: Router, returnUrl: string | null | undefined): void {
  const url = rewriteLocalhostToHostedUrl(returnUrl);
  if (!url) {
    void router.navigateByUrl('/tiers/ticketCaisse/tous');
    return;
  }
  if (/^https?:\/\//i.test(url)) {
    window.location.href = withCrossFrontAuth(url);
    return;
  }
  void router.navigateByUrl(url);
}
